/* SecurityDigestCard — admin overview card for the daily access-changes
 * digest. Mirrors `LowStockDigestCard` from the Services tab but reads
 * /api/academy/admin/security/access-digest, which aggregates revoke /
 * restore / password-reset / sign-out audit rows over the last N hours.
 *
 * Cookie auth — the Admin shell already establishes a JWT session.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, ShieldOff, KeyRound, RefreshCw, Send, Mail, Clock, AlertTriangle, History, ExternalLink,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

function fmtAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function StatusChip({ status, total }) {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
        Sent · {total ?? 0}
      </span>
    );
  }
  if (status === 'skipped_no_events') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-50 text-gray-600 border border-gray-200">
        All quiet
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200">
      Skipped
    </span>
  );
}

function CountPill({ icon: Icon, label, value, tone }) {
  const tones = {
    red:     'bg-red-50 text-red-700 border-red-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-50 text-amber-800 border-amber-200',
    slate:   'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] border ${tones[tone] || tones.slate}`}>
      <Icon className="h-3 w-3" />
      {value}
      <span className="font-semibold opacity-70">{label}</span>
    </span>
  );
}

export default function SecurityDigestCard() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        fetch(`${API}/academy/admin/security/access-digest?window_hours=24`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
        fetch(`${API}/academy/admin/security/access-digest/log?limit=10`, { credentials: 'include' }).then((r) => r.ok ? r.json() : null),
      ]);
      if (p) setData(p);
      if (h) setHistory(h.rows || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendNow = useCallback(async () => {
    setSending(true);
    setSent(false);
    try {
      const r = await fetch(`${API}/academy/admin/security/access-digest/send-now?window_hours=24`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) {
        setSent(true);
        load();
      }
    } finally {
      setSending(false);
      setTimeout(() => setSent(false), 4000);
    }
  }, [load]);

  if (!data && !loading) return null;
  const counts = data?.counts || { revoke: 0, restore: 0, password_reset: 0, sign_out: 0 };
  const total = data?.total ?? 0;
  const recipient = data?.recipient || 'careers@zhelta.com';
  const hour = data?.schedule?.hour ?? 9;
  const tz = data?.schedule?.tz || 'UTC';
  const last = data?.last_run;
  const lastAgo = last?.ts ? fmtAgo(last.ts) : null;

  return (
    <section
      data-testid="security-digest-card"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <div className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl shrink-0 ${
            counts.revoke > 0 ? 'bg-red-50 text-red-700' :
            total > 0 ? 'bg-amber-50 text-amber-700' :
            'bg-emerald-50 text-emerald-700'
          }`}>
            {counts.revoke > 0 ? <ShieldOff className="h-5 w-5" /> :
             total > 0 ? <KeyRound className="h-5 w-5" /> :
             <ShieldCheck className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">
              Security access-changes digest
            </div>
            <div className="text-[15px] font-extrabold text-[#0e0e10] mt-0.5">
              {loading ? 'Auditing the last 24h…'
                : total > 0
                  ? `${total} access event${total === 1 ? '' : 's'} in the last 24h`
                  : 'No access changes in the last 24h — all quiet'}
            </div>
            <p className="text-[12px] text-[#6E6E73] mt-1 leading-snug">
              Daily Resend email at <span className="font-bold text-[#0e0e10]">{String(hour).padStart(2, '0')}:00 {tz}</span> to{' '}
              <span className="font-bold text-[#0e0e10]">{recipient}</span>{' '}
              (durable audit trail even if Teams goes down). Override with{' '}
              <code className="text-[10.5px] bg-[#F5F5F7] px-1 py-0.5 rounded">SECURITY_DIGEST_TO</code> env.
            </p>
            {total > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="security-digest-counts">
                {counts.revoke > 0  && <CountPill icon={ShieldOff} label="revoked"  value={counts.revoke}  tone="red" />}
                {counts.restore > 0 && <CountPill icon={ShieldCheck} label="restored" value={counts.restore} tone="emerald" />}
                {counts.password_reset > 0 && <CountPill icon={KeyRound} label="pw reset" value={counts.password_reset} tone="amber" />}
                {counts.sign_out > 0 && <CountPill icon={AlertTriangle} label="sign-outs" value={counts.sign_out} tone="slate" />}
              </div>
            )}
            {last && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#6E6E73]" data-testid="security-digest-last-run">
                <Clock className="h-3 w-3" />
                <span>
                  Last digest: <span className="font-bold text-[#0e0e10]">{lastAgo}</span>
                  {' · '}
                  <StatusChip status={last.status} total={last.total_events} />
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 justify-end flex-wrap">
          <button
            type="button"
            onClick={load}
            data-testid="security-digest-refresh"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Re-check
          </button>
          <button
            type="button"
            onClick={sendNow}
            disabled={sending}
            data-testid="security-digest-send-now"
            className={`inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-all ${
              sending
                ? 'bg-[#F5F5F7] text-[#9aa0a6] cursor-not-allowed'
                : 'text-[#0e0e10] hover:-translate-y-0.5'
            }`}
            style={!sending ? { background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' } : {}}
            title={`Send digest to ${recipient}`}
          >
            {sending
              ? <><RefreshCw className="w-3 h-3 animate-spin" /> Sending…</>
              : sent
                ? <><Mail className="w-3 h-3" /> Sent!</>
                : <><Send className="w-3 h-3" /> Send digest now</>}
          </button>
          {history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="security-digest-history-toggle"
              className="inline-flex items-center gap-1.5 text-[11.5px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#E5E5E7] hover:bg-[#fff8e7] hover:border-[#c9a227]/50 transition-colors"
            >
              <History className="w-3 h-3" />
              {showHistory ? 'Hide' : 'Show'} history ({history.length})
            </button>
          )}
        </div>
      </div>

      {/* Recent events preview — top 5 from the rolling 24h window. */}
      {!loading && total > 0 && (data?.recent || []).length > 0 && (
        <div className="border-t border-gray-100 bg-[#FAFAFA]" data-testid="security-digest-recent">
          <ul className="divide-y divide-gray-100">
            {(data.recent || []).slice(0, 5).map((r, i) => {
              const isEmail = typeof r.target === 'string' && r.target.includes('@');
              const ACTION = {
                'user.revoke_access':         { label: 'Revoked',         tone: 'bg-red-100 text-red-800 border-red-200',         Icon: ShieldOff },
                'user.restore_access':        { label: 'Restored',        tone: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icon: ShieldCheck },
                'user.password_reset':        { label: 'Password reset',  tone: 'bg-amber-100 text-amber-800 border-amber-200',   Icon: KeyRound },
                'admin.sign_out_everywhere':  { label: 'Sign-out',        tone: 'bg-slate-100 text-slate-800 border-slate-200',   Icon: AlertTriangle },
                'admin.emergency_sign_out':   { label: 'Emergency',       tone: 'bg-rose-100 text-rose-800 border-rose-200',     Icon: AlertTriangle },
              }[r.action] || { label: r.action, tone: 'bg-gray-50 text-gray-700 border-gray-200', Icon: AlertTriangle };
              return (
                <li
                  key={`${r.ts}-${i}`}
                  className="px-5 py-2.5 grid grid-cols-[auto_1fr_auto] items-center gap-3 text-[12px]"
                  data-testid={`security-digest-recent-row-${i}`}
                >
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ACTION.tone}`}>
                    <ACTION.Icon className="h-3 w-3" />
                    {ACTION.label}
                  </span>
                  <div className="min-w-0">
                    <div className="font-bold text-[#0e0e10] truncate">
                      {isEmail ? (
                        <a
                          href={`/admin?tab=users&q=${encodeURIComponent(r.target)}`}
                          className="hover:text-[#c9a227] underline-offset-2 hover:underline inline-flex items-center gap-1"
                        >
                          {r.target}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (r.target || '—')}
                    </div>
                    <div className="text-[10.5px] text-[#9aa0a6] truncate mt-0.5">
                      by {r.actor_email || 'system'}
                    </div>
                  </div>
                  <span className="text-[10.5px] text-[#9aa0a6] tabular-nums shrink-0">{fmtAgo(r.ts)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {showHistory && history.length > 0 && (
        <div className="border-t border-gray-100 bg-white" data-testid="security-digest-history">
          <ul className="divide-y divide-gray-100">
            {history.map((row, idx) => (
              <li
                key={`${row.ts}-${idx}`}
                className="px-5 py-2.5 grid grid-cols-[auto_1fr_auto] items-center gap-3 text-[12px]"
              >
                <StatusChip status={row.status} total={row.total_events} />
                <div className="min-w-0">
                  <div className="font-bold text-[#0e0e10] truncate">
                    {row.status === 'sent'
                      ? `Sent to ${row.recipient || 'support'} · ${row.total_events ?? 0} events`
                      : row.status === 'skipped_no_events'
                        ? 'No access changes — email skipped (inbox-friendly)'
                        : `Skipped — ${row.error || 'unknown error'}`}
                  </div>
                  {row.sample_targets?.length > 0 && (
                    <div className="text-[10.5px] text-[#9aa0a6] font-mono truncate mt-0.5">
                      {row.sample_targets.join(' · ')}
                    </div>
                  )}
                </div>
                <span className="text-[10.5px] text-[#9aa0a6] tabular-nums shrink-0">{fmtAgo(row.ts)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
